import { createSlice, PayloadAction, Draft } from '@reduxjs/toolkit';

export interface GenericState<T> {
    data: T[];
    isFetching: boolean;
    error: string | null;
    isNetworkError?: boolean;
    hasFetchError?: boolean;
}

const createGenericSlice = <T>(name: string, additionalState: Partial<GenericState<T>> = {}) => {
    const initialState: GenericState<T> = {
        data: [],
        isFetching: false,
        error: null,
        isNetworkError: false,
        hasFetchError: false,
        ...additionalState,
    };

    return createSlice({
        name,
        initialState,
        reducers: {
            fetchDataStart(state) {
                state.isFetching = true;
                state.hasFetchError = false;
            },
            fetchDataError(state, action: PayloadAction<{ errorMessage: string; isNetworkError?: boolean; statusCode?: number } | string>) {
                if (typeof action.payload === 'string') {
                    state.error = action.payload;
                    state.isNetworkError = false;
                } else {
                    state.error = action.payload.errorMessage;
                    state.isNetworkError = action.payload.isNetworkError ?? false;

                    if (action.payload.statusCode === 500) {
                        console.warn("Server returned a 500 error:", action.payload.errorMessage);
                        state.error = action.payload.errorMessage;
                    }
                }
                state.isFetching = false;
            },
            setDataLoaded(state, action: PayloadAction<T[]>) {
                // Загрузка завершена в любом случае — иначе на пустом ответе
                // (например, история без попыток) UI завис бы в isFetching=true
                // и крутил спиннер вечно. Сам `data` пустым массивом не
                // перетираем — это сохраняет уже подгруженные страницы при
                // ленивой подгрузке, которая может вернуть пустой «хвост».
                state.isFetching = false;
                if (action.payload.length === 0) return;
                state.data = action.payload as Draft<T>[];
            },
            fetchDataSuccess(state) {
                state.isFetching = false;
            },
            deleteDataSuccess(state, action: PayloadAction<T>) {
                state.data = state.data.filter((item) => item !== action.payload);
                state.isFetching = false;
            },
            resetError(state) {
                state.error = null;
                state.isNetworkError = false;
            }
        },
    });
};

export default createGenericSlice;
